import * as Sentry from '@sentry/react';
import {
  isDiagnosticSentryMessage,
  shouldIgnoreSentryException,
  SENTRY_IGNORE_ERRORS,
} from './sentryFilters';

export interface SentryConfig {
  dsn?: string;
  environment?: string;
  release?: string;
  appName: string;
  enableInDevelopment?: boolean;
}

const isDevelopment = import.meta.env.DEV;
const isStandalone = window.matchMedia('(display-mode: standalone)').matches;

export function initSentry(config: SentryConfig): void {
  const {
    dsn = import.meta.env.VITE_SENTRY_DSN,
    environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || (isDevelopment ? 'development' : 'production'),
    release = import.meta.env.VITE_SENTRY_RELEASE,
    appName,
    enableInDevelopment = false,
  } = config;

  if (!dsn) {
    console.warn('[Sentry] No DSN provided, error tracking disabled');
    return;
  }

  if (isDevelopment && !enableInDevelopment) {
    console.log('[Sentry] Skipping initialization in development mode');
    return;
  }

  try {
    Sentry.init({
      dsn,
      environment,
      release: release || `${appName}@${import.meta.env.VITE_APP_VERSION || 'unknown'}`,

      integrations: [
        Sentry.browserTracingIntegration(),
        // Replay integration is added lazily after init to avoid blocking LCP
        // (see addReplayIntegrationLazily call below).
      ],

      tracesSampleRate: isDevelopment ? 1.0 : 0.1,
      replaysSessionSampleRate: isDevelopment ? 1.0 : 0.1,
      replaysOnErrorSampleRate: 1.0,

      beforeSend(event, hint) {
        if (isDevelopment && !enableInDevelopment) {
          console.log('[Sentry] Event captured:', event);
          return null;
        }

        const error = hint.originalException;
        const eventMessage = typeof event.message === 'string' ? event.message : '';
        const exceptionValue = event.exception?.values?.[0]?.value;

        if (error != null && shouldIgnoreSentryException(error)) {
          return null;
        }
        if (eventMessage && (isDiagnosticSentryMessage(eventMessage) || shouldIgnoreSentryException(eventMessage))) {
          return null;
        }
        if (
          typeof exceptionValue === 'string' &&
          (isDiagnosticSentryMessage(exceptionValue) || shouldIgnoreSentryException(exceptionValue))
        ) {
          return null;
        }

        if (error && typeof error === 'object') {
          const errorString = error.toString().toLowerCase();
          const stackString = ((error as { stack?: string }).stack || '').toLowerCase();

          // Drop AbortError originating from @supabase/gotrue-js locks.js
          if (
            (errorString.includes('aborterror') || (error as { name?: string }).name === 'AbortError') &&
            (stackString.includes('gotrue-js') || stackString.includes('/lib/locks.'))
          ) {
            return null;
          }

          // Suppress transient edge-function errors that the UI handles gracefully
          // and that are typically caused by user-side conditions (expired auth
          // token, network hiccup, cold start, FPL rate limiting, etc.) rather
          // than actionable bugs.
          const isFunctionsHttpError = errorString.includes('functionshttperror') ||
            errorString.includes('edge function returned a non-2xx status code');
          if (isFunctionsHttpError) {
            const breadcrumbs = event.breadcrumbs || [];
            const pageUrl = (
              (typeof event.request?.url === 'string' ? event.request.url : '') ||
              (typeof window !== 'undefined' ? window.location.href : '')
            ).toLowerCase();

            const matchesAny = (needles: string[]) =>
              breadcrumbs.some(b =>
                needles.some(n =>
                  b.message?.toLowerCase().includes(n) ||
                  (typeof b.data?.url === 'string' && b.data.url.toLowerCase().includes(n))
                )
              ) ||
              needles.some(n => errorString.includes(n)) ||
              needles.some(n => pageUrl.includes(n));

            if (matchesAny(['sync-fixtures', 'fpl-proxy'])) {
              return null;
            }
            if (matchesAny(['wc-leaderboard', '/worldcup', '/public/worldcup'])) {
              return null;
            }
            // Phone verification: UI already shows the server/user-facing message
            if (matchesAny(['send-phone-verification', 'verify-phone-code', 'sendverificationcode', 'verifyphonecode'])) {
              return null;
            }
          }
        }

        return event;
      },

      ignoreErrors: SENTRY_IGNORE_ERRORS,
    });

    Sentry.setTag('app', appName);
    Sentry.setTag('display_mode', isStandalone ? 'standalone' : 'browser');

    if (isStandalone) {
      Sentry.setContext('pwa', {
        standalone: true,
        userAgent: navigator.userAgent,
      });
    }

    console.log(`[Sentry] Initialized for ${appName} in ${environment} mode`);

    // Defer the Replay integration (~30 KiB) until the browser is idle so it
    // doesn't block initial page paint / LCP. Functionally identical — Replay
    // will still capture any error after it's attached (replaysOnErrorSampleRate=1.0).
    addReplayIntegrationLazily();
  } catch (error) {
    console.error('[Sentry] Failed to initialize:', error);
  }
}

/**
 * Adds the Sentry Replay integration after the browser is idle.
 * Replay is the largest single Sentry feature (~30 KiB) and is only sampled
 * for a fraction of sessions, so deferring it keeps it out of the LCP path.
 */
function addReplayIntegrationLazily(): void {
  const attach = () => {
    try {
      Sentry.addIntegration(
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        })
      );
    } catch (e) {
      console.warn('[Sentry] Failed to attach Replay integration:', e);
    }
  };

  const w = window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  };

  if (typeof w.requestIdleCallback === 'function') {
    w.requestIdleCallback(attach, { timeout: 3000 });
  } else {
    // Safari / older browsers: fall back to a short timeout after load.
    setTimeout(attach, 2000);
  }
}

export function setUserContext(userId: string | null, email?: string, metadata?: Record<string, any>): void {
  if (userId) {
    Sentry.setUser({
      id: userId,
      email,
      ...metadata,
    });
  } else {
    Sentry.setUser(null);
  }
}

export function setOrganizationContext(organizationId: string | null, organizationName?: string): void {
  if (organizationId) {
    Sentry.setTag('organization_id', organizationId);
    if (organizationName) {
      Sentry.setTag('organization_name', organizationName);
    }
    Sentry.setContext('organization', {
      id: organizationId,
      name: organizationName,
    });
  } else {
    Sentry.setTag('organization_id', undefined);
    Sentry.setTag('organization_name', undefined);
    Sentry.setContext('organization', null);
  }
}

export function setCompetitionContext(competitionId: string | null, competitionName?: string): void {
  if (competitionId) {
    Sentry.setTag('competition_id', competitionId);
    if (competitionName) {
      Sentry.setTag('competition_name', competitionName);
    }
    Sentry.setContext('competition', {
      id: competitionId,
      name: competitionName,
    });
  } else {
    Sentry.setTag('competition_id', undefined);
    Sentry.setTag('competition_name', undefined);
    Sentry.setContext('competition', null);
  }
}

export function addBreadcrumb(message: string, category: string, level: Sentry.SeverityLevel = 'info', data?: Record<string, any>): void {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
    timestamp: Date.now() / 1000,
  });
}

export function captureError(error: Error, context?: Record<string, any>, level: Sentry.SeverityLevel = 'error'): string {
  if (shouldIgnoreSentryException(error)) {
    if (isDevelopment) {
      console.error('[Error - not reported to Sentry]', error, context);
    }
    return '';
  }

  if (isDevelopment) {
    console.error('[Error]', error, context);
  }

  return Sentry.captureException(error, {
    level,
    contexts: context ? { custom: context } : undefined,
  });
}

export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info', context?: Record<string, any>): string {
  if (isDiagnosticSentryMessage(message) || shouldIgnoreSentryException(message)) {
    if (isDevelopment) {
      console.log(`[${level.toUpperCase()} - not reported to Sentry]`, message, context);
    }
    return '';
  }

  if (isDevelopment) {
    console.log(`[${level.toUpperCase()}]`, message, context);
  }

  return Sentry.captureMessage(message, {
    level,
    contexts: context ? { custom: context } : undefined,
  });
}

export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: React.ComponentType<{ error: Error; resetError: () => void }>,
  options?: {
    showDialog?: boolean;
    dialogOptions?: Sentry.ReportDialogOptions;
  }
): React.ComponentType<P> {
  return Sentry.withErrorBoundary(Component, {
    fallback: fallback as any,
    showDialog: options?.showDialog,
    dialogOptions: options?.dialogOptions,
  });
}

export { Sentry };
